import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { Link as RouterLink } from "@tanstack/react-router";
import {
  type Dispatch,
  memo,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "@/components/button";
import { Link } from "@/components/link";
import {
  type PaginationMetadata,
  DraggableTable as Table,
} from "@/components/table";
import { useTableSorting } from "@/hooks/useTableSorting";
import { dateFormatter } from "@/utils/dateFormatter";

import { COLUMN_WIDTHS } from "../utils/columnWidths";
import ReportModal from "./ReportModal";

export type RequestReportListType = {
  createdAt: Date | null;
  customerName: null | string;
  designerId: null | number;
  designerName: null | string;
  feedback: null | string;
  id: null | number;
  projectId: null | number;
  projectName: null | string;
  reasons:
    | boolean
    | null
    | number
    | Record<string, unknown>
    | string
    | unknown[];
  requestId: null | number;
  requestName: null | string;
};

interface IssueReportsTableProps {
  isLoading?: boolean;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  requestReports: RequestReportListType[];
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
}

const UserNameCell = memo(function UserNameCell({
  userId,
  userName,
}: {
  userId: string | undefined;
  userName: null | string;
}) {
  return (
    <RouterLink
      className="hover:no-underline"
      params={{ userId: String(userId) }}
      to="/users/$userId/edit"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
          {userName ?? "-"}
        </p>
      </div>
    </RouterLink>
  );
});

const RequestNameCell = memo(function RequestNameCell({
  id,
  name,
}: {
  id: null | number;
  name: null | string;
}) {
  return (
    <Link className="hover:no-underline" href={`/requests/${id}/edit`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
          {name ?? "-"}
        </p>
      </div>
    </Link>
  );
});

const AccountCell = memo(function AccountCell({
  id,
  name,
}: {
  id: null | number;
  name: null | string;
}) {
  return (
    <Link className="hover:no-underline" href={`/projects/${id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
          {name ?? "-"}
        </p>
      </div>
    </Link>
  );
});

const ReasonsCell = memo(function ReasonsCell({
  value,
}: {
  value: boolean | null | number | Record<string, unknown> | string | unknown[];
}) {
  if (Array.isArray(value)) {
    return (
      <ul className="list-disc list-inside  py-3 pr-3 min-w-0 w-full overflow-hidden">
        {value.length > 0
          ? value.map((item, idx) => (
              <li className="truncate" key={idx}>
                {String(item)}
              </li>
            ))
          : "-"}
      </ul>
    );
  }
});

function FeedbackCell({
  setFeedback,
  text,
}: {
  setFeedback: Dispatch<SetStateAction<{ openModal: boolean; text: string }>>;
  text: null | string;
}) {
  const limit = 100;

  if (!text) return null;
  const isLong = text.length > limit;
  const displayText = isLong ? text.slice(0, limit) + "..." : text;

  return (
    <div>
      <p className="mt-2">{displayText}</p>
      {isLong && (
        <Button
          className="text-blue-500 pb-2 hover:underline"
          onPress={() => setFeedback({ openModal: true, text })}
          variant="unstyled"
        >
          See more
        </Button>
      )}
    </div>
  );
}

const CustomerCell = memo(function FeedbackCell({
  value,
}: {
  value: null | string;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      {value ?? "-"}
    </div>
  );
});

const DateCell = memo(function DateCell({ value }: { value: string }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">
        {value ? dateFormatter(value) : "-"}
      </span>
    </div>
  );
});

const createColumn = (
  key: string,
  header: string,
  cell: ColumnDef<RequestReportListType>["cell"],
  options: {
    enableSorting?: boolean;
    width?: number;
  } = {},
): ColumnDef<RequestReportListType> => ({
  accessorKey: key,
  cell,
  enableSorting: options.enableSorting ?? true,
  header,
  maxSize: options.width,
  minSize: options.width,
  size: options.width,
});

export const IssueReportsTable = memo(function ViewTable({
  isLoading = false,
  pagination,
  requestReports,
  setSortDescriptor,
  sortDescriptor,
}: IssueReportsTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const [seeFeedback, setSeeFeedback] = useState({
    openModal: false,
    text: "",
  });

  const { handleSort, sorting } = useTableSorting({
    defaultColumn: "createdAt",
    defaultDirection: "descending",
    setSortDescriptor,
    sortDescriptor,
  });

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current.querySelector("table");

      if (table) {
        table.style.tableLayout = "fixed";
        table.style.width = "100%";
      }

      const cells = tableRef.current.querySelectorAll("td");

      for (const cell of cells) {
        cell.style.overflow = "hidden";
        cell.style.maxWidth = "0";
      }
    }
  }, []);

  const columns: ColumnDef<RequestReportListType>[] = [
    createColumn(
      "request",
      "Name",
      ({ row }) => (
        <RequestNameCell id={row.original.id} name={row.original.requestName} />
      ),
      { width: COLUMN_WIDTHS.NAME },
    ),
    createColumn(
      "designer",
      "Designer",
      ({ row }) => (
        <UserNameCell
          userId={row?.original?.designerId?.toString()}
          userName={row.original.designerName}
        />
      ),
      { width: COLUMN_WIDTHS.DESIGNER },
    ),
    createColumn(
      "reasons",
      "Reasons",
      ({ row }) => <ReasonsCell value={row.original.reasons} />,
      { width: COLUMN_WIDTHS.REASONS },
    ),
    createColumn(
      "feedback",
      "Feedback",
      ({ row }) => (
        <FeedbackCell
          setFeedback={setSeeFeedback}
          text={row.original.feedback}
        />
      ),
      { width: COLUMN_WIDTHS.FEEDBACK },
    ),
    createColumn(
      "account",
      "Account",
      ({ row }) => (
        <AccountCell
          id={row.original.projectId}
          name={row.original.projectName}
        />
      ),
      { width: COLUMN_WIDTHS.ACCOUNT },
    ),
    createColumn(
      "customer",
      "Customer",
      ({ row }) => <CustomerCell value={row.original.customerName} />,
      { width: COLUMN_WIDTHS.CUSTOMER },
    ),
    createColumn(
      "createdAt",
      "CreatedAt",
      ({ getValue }) => <DateCell value={getValue() as string} />,
      { width: COLUMN_WIDTHS.CREATED_ON },
    ),
  ];

  const getRowId = useCallback(
    (row: RequestReportListType) => String(row.id),
    [],
  );

  const paginationConfig = useMemo(
    () =>
      pagination
        ? {
            metadata: pagination.metadata,
            onItemsPerPageChange: pagination.onItemsPerPageChange,
            onPageChange: pagination.onPageChange,
            pageSizeOptions: pagination.pageSizeOptions,
            showPageSizeSelector: pagination.showPageSizeSelector,
          }
        : undefined,
    [
      pagination?.metadata,
      pagination?.onItemsPerPageChange,
      pagination?.onPageChange,
      pagination?.pageSizeOptions,
      pagination?.showPageSizeSelector,
    ],
  );

  const sortableColumns = [
    "reasons",
    "request",
    "designer",
    "feedback",
    "createdAt",
    "customer",
    "account",
  ];

  const onOpenChange = () => {
    setSeeFeedback({ openModal: false, text: seeFeedback.text });
  };

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto" ref={tableRef}>
      <ReportModal
        isOpen={seeFeedback.openModal}
        onOpenChange={onOpenChange}
        text={seeFeedback.text}
      />
      <Table<RequestReportListType>
        className="rounded-b-xl users-table w-full"
        columns={columns}
        data={requestReports}
        getRowId={getRowId}
        isLoading={isLoading}
        onSort={handleSort}
        pagination={paginationConfig}
        sortableColumns={sortableColumns}
        sorting={sorting}
      />
    </div>
  );
});
